import { LabelWithoutRef } from '../label/label-without-ref';
import { RuleBuilder } from '../rule-builder';
import { SummaryCard } from './summary-card';

import { cn } from '../../utils/cn';

import type { TConditionsOutput, TFact } from '../rule-builder/types';
import { TableResponsiveWrapper } from '../table/table-responsive-wrapper';
import type { Rule } from './types';

type TRuleViewer = {
  className?: string;
  facts?: TFact[];
  rule: Rule;
};

type TNormalizedVariant = {
  description: string;
  id: string;
  name: string;
  value: {
    conditions: TConditionsOutput;
    event?: { type: string };
  };
};

type TNormalizedRule = {
  active: boolean;
  createdAt: string;
  description: string;
  id: string;
  modifiedAt: string;
  modifiedBy: string;
  name: string;
  value: {
    conditions?: TConditionsOutput;
    event?: { type: string };
  };
  validationErrors: string[];
  variants: TNormalizedVariant[];
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isConditionNode(value: unknown): boolean {
  if (!isObject(value)) return false;

  const { fact, operator, isBaseCondition } = value;

  return (
    typeof fact === 'string' &&
    typeof operator === 'string' &&
    'value' in value &&
    (isBaseCondition === undefined || typeof isBaseCondition === 'boolean')
  );
}

function isGroupNode(value: unknown, depth = 0): value is TConditionsOutput {
  if (!isObject(value) || depth > 50) return false;

  const all = value.all;
  if (Array.isArray(all)) {
    return all.every((node) => isConditionNode(node) || isGroupNode(node, depth + 1));
  }

  const any = value.any;
  if (Array.isArray(any)) {
    return any.every((node) => isConditionNode(node) || isGroupNode(node, depth + 1));
  }

  const not = value.not;
  if (not !== undefined) {
    return isConditionNode(not) || isGroupNode(not, depth + 1);
  }

  return false;
}

function normalizeStringField(
  value: unknown,
  fallback: string,
  fieldName: string,
  errors: string[],
): string {
  if (typeof value === 'string') return value;

  errors.push(`El campo "${fieldName}" no es string.`);
  return fallback;
}

function normalizeRuleEvent(value: unknown): { type: string } | undefined | null {
  if (value === undefined) return undefined;
  if (isObject(value) && typeof value.type === 'string') return { type: value.type };
  return null;
}

function normalizeRuleData(rawRule: unknown): TNormalizedRule {
  const validationErrors: string[] = [];
  const fallbackId = 'rule-corrupt';

  if (!isObject(rawRule)) {
    return {
      active: false,
      createdAt: '-',
      description: '-',
      id: fallbackId,
      modifiedAt: '-',
      modifiedBy: '-',
      name: '-',
      value: {},
      validationErrors: ['El payload de la regla no es un objeto válido.'],
      variants: [],
    };
  }

  const id = normalizeStringField(rawRule.id, fallbackId, 'id', validationErrors);
  const name = normalizeStringField(rawRule.name, '-', 'name', validationErrors);
  const description = normalizeStringField(
    rawRule.description,
    '-',
    'description',
    validationErrors,
  );
  const createdAt = normalizeStringField(rawRule.createdAt, '-', 'createdAt', validationErrors);
  const modifiedAt = normalizeStringField(rawRule.modifiedAt, '-', 'modifiedAt', validationErrors);
  const modifiedBy = normalizeStringField(rawRule.modifiedBy, '-', 'modifiedBy', validationErrors);

  const active =
    typeof rawRule.active === 'boolean'
      ? rawRule.active
      : (() => {
          validationErrors.push('El campo "active" no es boolean.');
          return false;
        })();

  const value = isObject(rawRule.value) ? rawRule.value : null;
  if (!value) {
    validationErrors.push('El campo "value" no tiene el formato esperado.');
  }

  const conditions = value && isGroupNode(value.conditions) ? value.conditions : undefined;
  if (!conditions) {
    validationErrors.push(
      'La regla principal no tiene "value.conditions" válido. Se omite el montaje de RuleBuilder.',
    );
  }

  const event = normalizeRuleEvent(value?.event);

  if (value && value.event !== undefined && !event) {
    validationErrors.push('El campo opcional "value.event" no cumple con { type: string }.');
  }

  let variants: TNormalizedVariant[] = [];
  if (rawRule.variants === undefined) {
    variants = [];
  } else if (!Array.isArray(rawRule.variants)) {
    validationErrors.push('El campo "variants" no es un array.');
  } else {
    variants = rawRule.variants.reduce<TNormalizedVariant[]>((acc, variant, index) => {
      if (!isObject(variant)) {
        validationErrors.push(`La variante #${index + 1} no es un objeto válido.`);
        return acc;
      }

      const variantId = normalizeStringField(
        variant.id,
        `variant-${index + 1}`,
        `variants[${index}].id`,
        validationErrors,
      );
      const variantName = normalizeStringField(
        variant.name,
        '-',
        `variants[${index}].name`,
        validationErrors,
      );
      const variantDescription = normalizeStringField(
        variant.description,
        '-',
        `variants[${index}].description`,
        validationErrors,
      );

      if (!isObject(variant.value)) {
        validationErrors.push(`La variante "${variantId}" no tiene "value" válido.`);
        return acc;
      }

      if (!isGroupNode(variant.value.conditions)) {
        validationErrors.push(
          `La variante "${variantId}" tiene "value.conditions" inválido. Se omite su RuleBuilder.`,
        );
        return acc;
      }

      const variantEvent = normalizeRuleEvent(variant.value.event);

      if (variant.value.event !== undefined && !variantEvent) {
        validationErrors.push(
          `La variante "${variantId}" tiene "value.event" inválido y se ignorará ese campo.`,
        );
      }

      acc.push({
        description: variantDescription,
        id: variantId,
        name: variantName,
        value: {
          conditions: variant.value.conditions,
          event: variantEvent ?? undefined,
        },
      });

      return acc;
    }, []);
  }

  return {
    active,
    createdAt,
    description,
    id,
    modifiedAt,
    modifiedBy,
    name,
    value: {
      conditions,
      event: event ?? undefined,
    },
    validationErrors,
    variants,
  };
}

export function RuleViewer({ className, facts = [], rule }: TRuleViewer) {
  const normalizedRule = normalizeRuleData(rule as unknown);
  const { active, createdAt, description, id, modifiedAt, modifiedBy, name, value, variants } =
    normalizedRule;

  return (
    <div className={cn('w-full space-y-8', className)}>
      {normalizedRule.validationErrors.length > 0 ? (
        <div className="rounded-3xl border border-destructive bg-destructive/5 p-4 text-sm text-destructive">
          <p className="font-medium">Inconsistencias detectadas</p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {normalizedRule.validationErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <SummaryCard
        active={active}
        createdAt={createdAt}
        description={description}
        id={id}
        modifiedAt={modifiedAt}
        modifiedBy={modifiedBy}
        name={name}
        variants={variants}
      />

      <div>
        <LabelWithoutRef>Regla</LabelWithoutRef>
        {value.conditions ? (
          <TableResponsiveWrapper>
            <RuleBuilder
              key={`rule-${id}`}
              defaultValue={value.conditions}
              disabled
              facts={facts}
              kind={variants.length > 0 ? 'rule-with-variants' : 'rule'}
              responsive={false}
            />
          </TableResponsiveWrapper>
        ) : (
          <p className="text-sm text-destructive">
            No se puede renderizar la regla porque su estructura no cumple con el formato esperado.
          </p>
        )}
      </div>

      {variants.length > 0 ? (
        <div className="space-y-8">
          <LabelWithoutRef>Variantes</LabelWithoutRef>
          <div className="flex flex-col gap-8">
            {variants.map((variant, index) => (
              <div key={variant.id}>
                <div>
                  <LabelWithoutRef>
                    {index + 1}. {variant.name}
                  </LabelWithoutRef>
                  <p className="text-muted-foreground">{variant.description}</p>
                </div>

                <TableResponsiveWrapper>
                  <RuleBuilder
                    key={`variant-${variant.id}`}
                    defaultValue={variant.value.conditions}
                    disabled
                    facts={facts}
                    responsive={false}
                  />
                </TableResponsiveWrapper>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
